import { Component } from '@theme/component';
import { animateHeight } from '@theme/r-motion';
import { track } from '@theme/r-analytics';

/** Slack for the overflow comparison, in px — line metrics are sub-pixel. */
const FIT_TOLERANCE = 4;

/**
 * <r-read-more> — "Show more / Show less" for clamped prose.
 *
 * Visibility of the toggle is CSS-driven (see r-pdp-description's stylesheet),
 * so it works even if this script never runs. This element:
 *   - flips `data-expanded` on click and keeps `aria-expanded` in sync;
 *   - sets `data-fits` when the collapsed copy is short enough not to need a
 *     toggle, so the toggle can hide itself for short descriptions;
 *   - animates the open/close, which CSS alone cannot do for a clamp.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} button - the show more/less toggle
 * @property {HTMLElement} content - the clamped content wrapper
 * @extends {Component<Refs>}
 */
class RReadMore extends Component {
  requiredRefs = ['button', 'content'];

  /** @type {ResizeObserver | null} */
  #resizeObs = null;

  /** Host width at the last measure. @type {number} */
  #lastWidth = -1;

  /** @type {number} */
  #rafId = 0;

  /** The in-flight open/close, if any. @type {Animation | null} */
  #animation = null;

  connectedCallback() {
    super.connectedCallback();

    // Layout is not settled on the first connected tick (fonts, images).
    // Defer a frame; the observers below keep it honest afterwards.
    this.#rafId = requestAnimationFrame(this.#measure);

    // ↗ .cursor/references/code-notes/r-read-more.js.md#§1
    if ('ResizeObserver' in window) {
      this.#resizeObs = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width === this.#lastWidth) return;
        this.#lastWidth = width;
        this.#measure();
      });
      this.#resizeObs.observe(this);
    }

    // Web fonts change the line count after first paint.
    if (document.fonts?.ready) {
      document.fonts.ready
        .then(() => {
          if (this.isConnected) this.#measure();
        })
        .catch(() => {});
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#rafId);
    this.#animation?.cancel();
    this.#animation = null;
    this.#resizeObs?.disconnect();
    this.#resizeObs = null;
    super.disconnectedCallback();
  }

  /**
   * Toggle expanded state.
   * @param {Event} [event]
   */
  toggle = (event) => {
    event?.preventDefault();
    const { content } = this.refs;

    // ↗ .cursor/references/code-notes/r-read-more.js.md#§2
    const from = content.getBoundingClientRect().height;

    const expanded = this.dataset.expanded === 'true';
    this.dataset.expanded = expanded ? 'false' : 'true';
    this.refs.button.setAttribute('aria-expanded', expanded ? 'false' : 'true');

    // Collapsing re-applies the clamp, so whether the toggle is still needed
    // is an open question again. Settle it before measuring the target height.
    if (expanded) this.#measure();
    else this.#trackExpand();

    this.#animateFrom(from);
  };

  /**
   * Play the open/close over the two measured heights.
   *
   * `animateHeight` from @theme/r-motion is the sanctioned in-flow collapse: a
   * Web Animation rather than a CSS transition, because the target height can
   * only be read by flushing layout at the target, which lands the element there
   * before a transition could start from the old value. An animation does not
   * care what the element's previous computed style was, so both directions work
   * from one code path. It reads its duration and easing from the element's own
   * computed transition, so the height and the bottom fade cannot drift apart —
   * and because the stylesheet swaps to the DISCLOSURE exit pair while collapsed,
   * the close comes back faster and on the cubic-in for free. `fill` stays at its
   * default of `none`, so the moment it finishes the stylesheet owns the height
   * again — which is already the value the animation ended on.
   *
   * @param {number} from - content height before the state flipped, in px
   */
  #animateFrom(from) {
    const { content } = this.refs;

    this.#animation?.cancel();
    this.#animation = null;

    const to = content.getBoundingClientRect().height;
    if (Math.abs(to - from) < 1) return;

    const animation = animateHeight(content, from, to);
    if (!animation) return;

    this.#animation = animation;

    animation.finished
      .then(() => {
        if (this.#animation !== animation) return;
        this.#animation = null;
        // The clamp may have become unnecessary while it was open.
        this.#measure();
      })
      .catch(() => {
        // Cancelled by a reversal or a disconnect; the newer run owns the state.
        if (this.#animation === animation) this.#animation = null;
      });
  }

  /** WS23 — an expand inside an editorial story is story_engage; anywhere else it is pdp_engage. */
  #trackExpand() {
    const story = /** @type {HTMLElement | null} */ (this.closest('[data-r-story-id]'));
    if (story) {
      track('story_engage', { block: 'text_expand', story_id: story.dataset.rStoryId });
      return;
    }
    track('pdp_engage', {
      section: this.dataset.rTrackSection ?? 'description_more',
      product_id: document.querySelector('[data-r-track-product-id]')?.getAttribute('data-r-track-product-id') ?? undefined,
    });
  }

  /** Flag `data-fits` when the collapsed content does not overflow its clamp. */
  #measure = () => {
    // Mid-animation the height is being driven frame by frame and says nothing
    // about whether the copy overflows; onfinish re-measures.
    if (this.#animation) return;

    const { content } = this.refs;

    // While expanded the content is unclamped; keep the toggle available so the
    // reader can collapse again.
    if (this.dataset.expanded === 'true') {
      this.removeAttribute('data-fits');
      return;
    }

    // ↗ .cursor/references/code-notes/r-read-more.js.md#§3
    this.removeAttribute('data-fits');
    const fits = content.scrollHeight - content.clientHeight <= FIT_TOLERANCE;
    this.toggleAttribute('data-fits', fits);
  };
}

if (!customElements.get('r-read-more')) {
  customElements.define('r-read-more', RReadMore);
}
